/**
 * CONVERTED_SECTIONS — Brand Bible v2 sections that have adopted the
 * explicit-save contract (edits buffer locally; a Save button commits them;
 * a dirty guard blocks navigating away without resolving them). See
 * ai-docs/architect-briefs/bb-phase2-explicit-save-brief-20260810.md.
 *
 * Per-section slices (bb-phase2-explicit-save-brief-20260810, "Per-section
 * conversion") add letters one at a time — F -> G -> H -> I, then A-E — each
 * only once its own conversion (buffered edits, Save/Discard wired, tests
 * green) is done. A letter added here prematurely would silence that section's
 * autosave with no Save path behind it (silent data loss). Do not add a letter
 * until its buffering + Save path is proven end-to-end in that section's own
 * conversion PR.
 */
import type { BbLetter } from '@/lib/bbSectionMap';

export const CONVERTED_SECTIONS: ReadonlySet<BbLetter> = new Set<BbLetter>([
  /**
   * A — Essentials / Identity. SectionEssentials buffers every §A field edit
   * (essentialsBuffer) and commits on the page's "Save A" button: the
   * draft-backed dual-write scalars (business_name, website, phone, email,
   * description, address, service_locations) commit through the commitDraftFields
   * seam (flat legacy column + bb_identity/bb_locations via pushDraftToV2); the
   * v2-only fields (dba/founded/team/booking, owner, socials, primary-location
   * radius/tracking_phone/gbp_url/hours) replay as narrow saveIdentity/
   * savePrimaryLocation patches. Verify / unverify / "Verify all" stay immediate.
   * §A owns the four flat brand_bible columns those draft scalars back
   * (business_name, description, contact_info, service_locations) — see
   * lib/bbLegacyFieldOwnership — so the page autosave stops persisting them and
   * §A's Save (with includeLetters=['A']) becomes their only write path.
   */
  'A',
  // §B (Voice Profile) — converted 2026-08-17. §B adds NO entry to
  // lib/bbLegacyFieldOwnership: every §B field is v2-homed (bb_voice_profile,
  // bb_chunks), so B joining this set silences no autosave path at all. In
  // particular `tone_of_writing` is NOT §B's to own — §7.6 already excludes it
  // from the v2 payload, and for non-v2 tenants it stays fully editable through
  // the legacy path, so claiming it here would strip a live write.
  'B',
  /**
   * C — Offers & pricing facts. OffersPanel owns the registration and buffers
   * every `bb_offers` content edit (guarantees, dispatch fee, financing,
   * promotions); Verify and approved-for-public stay immediate. §C owns NO
   * flat brand_bible payload columns yet, so this letter strips nothing from
   * the page autosave — see bbLegacyFieldOwnership. `services_offered` (the
   * chips) and the bb_services detail fields are still immediate and land in
   * §C's second slice, which is when this letter gains a flat-column entry.
   */
  'C',
  // §D — Proof Library (bb_review_stats / bb_credentials / bb_case_studies).
  // Buffered in ProofLibraryPanel + proofBuffer, committed by the panel's
  // registered save(). §D owns no flat brand_bible columns, so it adds no
  // BB_FLAT_COLUMN_OWNER entries — see the note in lib/bbLegacyFieldOwnership.
  'D',
  /**
   * E — Market & Customer. SectionAudience buffers the three convertible
   * legacy_only columns it owns (customer_pain_points, website_audience,
   * buyer_personas) and commits them via the page's commitDraftFields seam;
   * chunk-row Verify/Accept stay immediate. §E DOES add BB_FLAT_COLUMN_OWNER
   * entries (as §A and §F do; B/C/D/H claim none). `ideal_client` and
   * `seasonality` are NOT §E's to own: they are `context`-class with bb_chunks
   * homes and 422 on the flat path, so they never route through the payload and
   * there is nothing to strip. PR #1679 hides those controls on v2-active
   * tenants instead of buffering them behind a Save that could not persist.
   */
  'E',
  // §F — Competitive set. CompetitiveSetSection buffers the typed competitor
  // records (bb_chunks section='F' kind='competitor'); its Save commits them
  // through the existing chunk services. §F owns the flat `competitors`
  // column (write-only never-link mirror) — see lib/bbLegacyFieldOwnership.
  'F',
  /**
   * G — Compliance & Never List. The first section whose edits span TWO stores,
   * and both are buffered in CompliancePanel: the typed `bb_compliance_rules`
   * ops (create / patch / soft-delete, ID-KEYED — never derived from a list
   * diff) AND the three legacy flat fields (words_avoid, services_not_offered,
   * regulatory_notes) that compose `brand_restrictions`, committed through the
   * draft-commit seam. Verify / unverify stay immediate.
   * §G OWNS the flat `brand_restrictions` column — see lib/bbLegacyFieldOwnership
   * — so this letter is what stops the page autosaving it, and §G's Save becomes
   * its only write path. A null commit seam therefore fails save() loudly rather
   * than reporting success; that guard is mutation-tested.
   * Known limit, deliberate: discard() reverts the typed ops but NOT the three
   * legacy fields — those inputs are page-owned and this slice takes no page
   * edit. They clear via Save or by retyping the saved value; beforeunload
   * guards them meanwhile.
   */
  'G',
  /**
   * §H (Visual Identity) — feat/bb-save-h. VisualIdentityPanel buffers its six
   * bb_visual_identity fields and commits them on Save through the existing
   * singleton service (upsertEntry); its one legacy flat column ("Imagery &
   * references" = brand_bible.visual_identity) commits through the page's
   * useBbDraftCommit() seam. §H adds NO entry to BB_FLAT_COLUMN_OWNER on
   * purpose — see lib/bbLegacyFieldOwnership.ts ("Deliberately ABSENT") for why
   * visual_identity is not claimed (the still-present page-level imagery
   * textarea would be orphaned). So H stripping no flat column is correct: the
   * strip filter is a no-op for H, and none of §H's v2 fields are flat columns.
   */
  'H',
  /**
   * I — Channel Rules. Converted in feat/bb-save-i: adds, channel-tag edits and
   * removals buffer in ChannelRulesPanel and commit through the existing store
   * actions on Save I; Verify/unverify stay immediate. §I writes only bb_chunks
   * and owns NO flat brand_bible column, so it contributes no entry to
   * BB_FLAT_COLUMN_OWNER — there is nothing for the autosave-skip filter to
   * strip. See the note in lib/bbLegacyFieldOwnership.ts.
   */
  'I',
]);
